/**
 * Shared DNS record configuration for CruvzMail
 * 
 * Single source of truth for all DNS records shown to users.
 * Used by: DNSSetup, DomainVerification, and any future DNS-related UI.
 * 
 * When these values change, update the identical file in:
 *   - admin-panel/src/config/dns.ts
 *   - backend env vars: MAIL_HOSTNAME, MAIL_PUBLIC_IP
 */

export const MAIL_HOSTNAME = 'mail.cruvzmail.com';
export const MAIL_IP = '103.160.153.200';

export interface DnsRecord {
  type: string;
  host: string;
  value: string;
  priority?: number;
  ttl: string;
  description?: string;
  purpose?: string;
}

/**
 * Generate all required DNS records for a domain.
 * Called by signup flow DNSSetup, DomainVerification, and anywhere else DNS records are shown.
 */
export function generateDnsRecords(
  domainName: string,
  dkimSelector: string = 'default',
  dkimPublicKey: string = '',
): DnsRecord[] {
  return [
    // === REQUIRED RECORDS (Gmail/Outlook/Zoho all require these) ===
    {
      type: 'MX',
      host: '@',
      value: `${MAIL_HOSTNAME}.`,
      priority: 10,
      ttl: '1 Hour',
      description: 'Mail Exchange — Routes incoming mail to CruvzMail (Priority 10)',
      purpose: 'Mail server for receiving emails (Priority: 10)',
    },
    {
      type: 'TXT',
      host: '@',
      value: `v=spf1 ip4:${MAIL_IP} a:${MAIL_HOSTNAME} -all`,
      ttl: '1 Hour',
      description: 'SPF — Authorizes CruvzMail IP to send emails (REQUIRED)',
      purpose: 'SPF - Authorizes mail server to send emails (REQUIRED)',
    },
    {
      type: 'TXT',
      host: `${dkimSelector}._domainkey`,
      value: dkimPublicKey
        ? `v=DKIM1; h=sha256; k=rsa; p=${dkimPublicKey}`
        : 'DKIM record will be shown after domain is created — check admin panel',
      ttl: '1 Hour',
      description: 'DKIM — Email signature verification (unique per domain)',
      purpose: 'DKIM - Email signature verification (REQUIRED)',
    },
    {
      type: 'TXT',
      host: '_dmarc',
      value: `v=DMARC1; p=quarantine; sp=quarantine; adkim=r; aspf=r; pct=100; fo=1; rua=mailto:postmaster@${domainName}; ruf=mailto:postmaster@${domainName}`,
      ttl: '1 Hour',
      description: 'DMARC — Email authentication policy with forensic reports (REQUIRED)',
      purpose: 'DMARC - Authentication policy (REQUIRED for Gmail/Outlook/Zoho)',
    },
    {
      type: 'A',
      host: 'mail',
      value: MAIL_IP,
      ttl: '1 Hour',
      description: `A record — Points mail.${domainName} to CruvzMail server`,
      purpose: `A record for mail.${domainName}`,
    },
    // === RECOMMENDED RECORDS (improve deliverability & trust) ===
    {
      type: 'TXT',
      host: '_smtp._tls',
      value: `v=TLSRPTv1; rua=mailto:tls-reports@${domainName}`,
      ttl: '1 Hour',
      description: 'TLS-RPT — TLS failure reporting (improves Gmail/Outlook trust)',
      purpose: 'TLS-RPT - TLS failure reporting (Recommended)',
    },
  ];
}
